import React, { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { blogPosts } from "../../../data/blogData";
import DOMPurify from "dompurify";
import "./BlogPost.css";

const BlogPost = () => {
  const { id } = useParams();

  // Find post by id in the imported blogPosts array
  const post = blogPosts.find((p) => p.id === parseInt(id));

  // Sanitize HTML content to prevent XSS attacks
  const sanitizedContent = useMemo(() => {
    if (!post?.content) return "";
    return DOMPurify.sanitize(post.content, {
      ALLOWED_TAGS: [
        "p",
        "br",
        "strong",
        "em",
        "u",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "ul",
        "ol",
        "li",
        "a",
        "img",
        "blockquote",
        "code",
        "pre",
      ],
      ALLOWED_ATTR: ["href", "src", "alt", "title", "class"],
    });
  }, [post?.content]);

  if (!post) {
    return (
      <div className="blog-post-page">
        <div className="container">
          <h1>Post Not Found</h1>
          <p>The blog post you're looking for doesn't exist.</p>
          <Link to="/blog" className="back-to-blog">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-post-page">
      {/* Hero Section */}
      <section className="blog-post-hero">
        <div className="container">
          <Link to="/blog" className="back-to-blog">
            ← Back to Blog
          </Link>
          <div className="post-header">
            <div className="post-category">{post.category}</div>
            <h1>{post.title}</h1>
            <div className="post-meta">
              <span className="author">By {post.author}</span>
              <span className="date">{post.date}</span>
              <span className="read-time">{post.readTime}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="blog-post-image">
        <div className="container">
          <img src={post.image} alt={post.title} />
        </div>
      </section>

      {/* Post Content */}
      <section className="blog-post-content">
        <div className="container">
          <div
            className="content"
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          />
        </div>
      </section>

      {/* Related Posts */}
      <section className="related-posts">
        <div className="container">
          <h3>More Articles</h3>
          <div className="related-posts-grid">
            {blogPosts
              .filter((relatedPost) => relatedPost.id !== post.id)
              .slice(0, 3)
              .map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.id}`}
                  className="related-post-card"
                >
                  <div className="related-post-image">
                    <img src={relatedPost.image} alt={relatedPost.title} />
                    <div className="related-post-category">
                      {relatedPost.category}
                    </div>
                  </div>
                  <div className="related-post-content">
                    <h4>{relatedPost.title}</h4>
                    <div className="related-post-meta">
                      <span className="author">By {relatedPost.author}</span>
                      <span className="read-time">{relatedPost.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
